import React, { useState, useEffect, useRef } from 'react';
import { X, ZoomIn, ZoomOut, Check, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const ImageCropperModal = ({
  isOpen,
  onClose,
  imageFile,
  circular = false,
  aspectRatio = 1,
  onCrop,
  language = 'de'
}) => {
  const [imageSrc, setImageSrc] = useState('');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [baseScale, setBaseScale] = useState(1);
  const [loading, setLoading] = useState(true);

  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0 });

  // Viewport details
  const containerSize = 320;
  // Calculate crop box size within the 320x320 container
  let cropWidth = 240;
  let cropHeight = 240;
  if (aspectRatio !== 1) {
    if (aspectRatio > 1) {
      cropWidth = 280;
      cropHeight = 280 / aspectRatio;
    } else {
      cropHeight = 280;
      cropWidth = 280 * aspectRatio;
    }
  }

  // Load file as Data URL
  useEffect(() => {
    if (!imageFile) return;

    setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const handleImageLoaded = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setNaturalSize({ width: naturalWidth, height: naturalHeight });

    // Calculate base scale to fully cover the crop area
    const scaleX = cropWidth / naturalWidth;
    const scaleY = cropHeight / naturalHeight;
    const initialScale = Math.max(scaleX, scaleY);
    setBaseScale(initialScale);
    setLoading(false);
  };

  const handleZoomChange = (newZoom) => {
    const clampedZoom = Math.min(3, Math.max(1, newZoom));
    setZoom(clampedZoom);

    // Re-clamp offsets
    const currentScale = baseScale * clampedZoom;
    const maxX = Math.max(0, (naturalSize.width * currentScale - cropWidth) / 2);
    const maxY = Math.max(0, (naturalSize.height * currentScale - cropHeight) / 2);

    setOffset(prev => ({
      x: Math.min(maxX, Math.max(-maxX, prev.x)),
      y: Math.min(maxY, Math.max(-maxY, prev.y))
    }));
  };

  // Drag handlers (mouse & touch)
  const handleStart = (clientX, clientY) => {
    if (loading) return;
    dragRef.current = {
      active: true,
      startX: clientX - offset.x,
      startY: clientY - offset.y
    };
  };

  const handleMove = (clientX, clientY) => {
    if (!dragRef.current.active) return;
    const newX = clientX - dragRef.current.startX;
    const newY = clientY - dragRef.current.startY;

    const currentScale = baseScale * zoom;
    const maxX = Math.max(0, (naturalSize.width * currentScale - cropWidth) / 2);
    const maxY = Math.max(0, (naturalSize.height * currentScale - cropHeight) / 2);

    setOffset({
      x: Math.min(maxX, Math.max(-maxX, newX)),
      y: Math.min(maxY, Math.max(-maxY, newY))
    });
  };

  const handleEnd = () => {
    dragRef.current.active = false;
  };

  const executeCrop = () => {
    if (!imgRef.current) return;

    const currentScale = baseScale * zoom;
    // Calculate the coordinates of the crop area on the natural image
    // Center of container is (containerSize/2, containerSize/2)
    // Left of crop box relative to container:
    const cropLeft = (containerSize - cropWidth) / 2;
    const cropTop = (containerSize - cropHeight) / 2;

    // Image center position relative to container:
    const imgCenterLeft = containerSize / 2 + offset.x;
    const imgCenterTop = containerSize / 2 + offset.y;

    // Image top-left relative to container:
    const displayedWidth = naturalSize.width * currentScale;
    const displayedHeight = naturalSize.height * currentScale;
    const imgLeft = imgCenterLeft - displayedWidth / 2;
    const imgTop = imgCenterTop - displayedHeight / 2;

    // Offset of crop box from image top-left (in screen pixels)
    const dx = cropLeft - imgLeft;
    const dy = cropTop - imgTop;

    // Map to natural image dimensions
    const sourceX = dx * (naturalSize.width / displayedWidth);
    const sourceY = dy * (naturalSize.height / displayedHeight);
    const sourceWidth = cropWidth * (naturalSize.width / displayedWidth);
    const sourceHeight = cropHeight * (naturalSize.height / displayedHeight);

    // Draw on canvas
    const canvas = document.createElement('canvas');
    canvas.width = cropWidth * 2; // Export at double resolution for high quality
    canvas.height = cropHeight * 2;
    const ctx = canvas.getContext('2d');

    // Enable high-quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      imgRef.current,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      cropWidth * 2,
      cropHeight * 2
    );

    // Export as JPEG blob
    canvas.toBlob((blob) => {
      if (!blob) return;
      onCrop(blob);
      onClose();
    }, 'image/jpeg', 0.92);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in">
      <div className="w-full max-w-sm bg-card border border-border/80 shadow-2xl rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 bg-secondary/10">
          <span className="font-bold text-sm">
            {language === 'de' ? 'Ausschnitt wählen' : 'Select Crop'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-secondary"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Viewport container */}
        <div
          ref={containerRef}
          className="relative bg-muted/30 overflow-hidden select-none flex items-center justify-center"
          style={{ width: containerSize, height: containerSize, margin: '0 auto' }}
          onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
          onMouseMove={(e) => handleMove(e.clientX, e.clientY)}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={(e) => {
            if (e.touches[0]) handleStart(e.touches[0].clientX, e.touches[0].clientY);
          }}
          onTouchMove={(e) => {
            if (e.touches[0]) handleMove(e.touches[0].clientX, e.touches[0].clientY);
          }}
          onTouchEnd={handleEnd}
        >
          {imageSrc ? (
            <img
              ref={imgRef}
              src={imageSrc}
              alt="To Crop"
              onLoad={handleImageLoaded}
              draggable="false"
              className="absolute max-w-none max-h-none pointer-events-none select-none transition-transform duration-75"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: naturalSize.width * baseScale,
                height: naturalSize.height * baseScale,
                transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transformOrigin: 'center'
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
              <RefreshCw className="w-8 h-8 animate-spin mb-2" />
              <span className="text-xs">{language === 'de' ? 'Bild wird geladen...' : 'Loading image...'}</span>
            </div>
          )}

          {/* SVG Crop Overlay mask */}
          {!loading && (
            <svg className="absolute inset-0 pointer-events-none w-full h-full z-20">
              <defs>
                <mask id="crop-mask">
                  <rect width="100%" height="100%" fill="white" />
                  {circular ? (
                    <circle cx="50%" cy="50%" r={cropWidth / 2} fill="black" />
                  ) : (
                    <rect
                      x={(containerSize - cropWidth) / 2}
                      y={(containerSize - cropHeight) / 2}
                      width={cropWidth}
                      height={cropHeight}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="black" opacity="0.65" mask="url(#crop-mask)" />
              {/* Stroke guide */}
              {circular ? (
                <circle
                  cx="50%"
                  cy="50%"
                  r={cropWidth / 2}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.8)"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                />
              ) : (
                <rect
                  x={(containerSize - cropWidth) / 2}
                  y={(containerSize - cropHeight) / 2}
                  width={cropWidth}
                  height={cropHeight}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.8)"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                />
              )}
            </svg>
          )}
        </div>

        {/* Controls */}
        <div className="p-4 border-t border-border/40 space-y-4 bg-secondary/5">
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
              disabled={loading}
              className="w-full accent-primary cursor-pointer h-1.5 bg-border rounded-lg appearance-none"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button
              size="sm"
              onClick={executeCrop}
              disabled={loading}
              className="flex-1 gap-1.5 bg-green-600 hover:bg-green-700 text-white font-bold"
            >
              <Check className="w-4 h-4" />
              {language === 'de' ? 'Zuschneiden' : 'Crop'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropperModal;
